import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { DateTime } from "luxon";
import { WorkspaceEditScheduledArchiveAt } from "../../WorkspaceTodoSignalShared/WorkspaceEditScheduledArchiveAt";
import { WorkspaceSignalEditScheduledArchiveAtQuery } from "./__generated__/WorkspaceSignalEditScheduledArchiveAtQuery.graphql";
import { WorkspaceSignalEditScheduledArchiveAtSaveMutation } from "./__generated__/WorkspaceSignalEditScheduledArchiveAtSaveMutation.graphql";
import { WorkspaceSignalEditScheduledArchiveAtArchiveMutation } from "./__generated__/WorkspaceSignalEditScheduledArchiveAtArchiveMutation.graphql";
import { useWorkspaceSignalArchiveDescription } from "./useWorkspaceSignalArchiveDescription";

function InnerWorkspaceSignalEditScheduledArchiveAt(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalEditScheduledArchiveAt"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceSignalEditScheduledArchiveAtQuery>(
    graphql`
      query WorkspaceSignalEditScheduledArchiveAtQuery(
        $workspaceId: ID!
        $workspaceSignalId: ID!
      ) {
        workspace(id: $workspaceId) {
          signal(id: $workspaceSignalId) {
            scheduledArchiveAt {
              isoString
              ...FormattedDate
            }
          }
        }
      }
    `,
    {
      workspaceId: props.route.params.workspaceId,
      workspaceSignalId: props.route.params.workspaceSignalId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [save, saveLoading] =
    useMutation<WorkspaceSignalEditScheduledArchiveAtSaveMutation>(graphql`
      mutation WorkspaceSignalEditScheduledArchiveAtSaveMutation(
        $workspaceSignalId: ID!
        $scheduledArchiveAt: String
      ) {
        signalWorkspace(id: $workspaceSignalId) {
          updateScheduledArchiveAt(newArchiveAt: $scheduledArchiveAt) {
            id
          }
        }
      }
    `);

  const [archive, archiveLoading] =
    useMutation<WorkspaceSignalEditScheduledArchiveAtArchiveMutation>(graphql`
      mutation WorkspaceSignalEditScheduledArchiveAtArchiveMutation(
        $workspaceSignalId: ID!
      ) {
        signalWorkspace(id: $workspaceSignalId) {
          archive {
            id
            archivedAtDate {
              ...FormattedDate
            }
          }
        }
      }
    `);

  const onSave = useCallback(
    (archiveDate: DateTime | null) => {
      save({
        variables: {
          workspaceSignalId: props.route.params.workspaceSignalId,
          scheduledArchiveAt: archiveDate?.toISO() ?? null,
        },
        onCompleted: () => {
          props.navigation.goBack();
        },
      });
    },
    [props.route.params.workspaceSignalId, save, props.navigation]
  );

  const onArchive = useCallback(() => {
    archive({
      variables: {
        workspaceSignalId: props.route.params.workspaceSignalId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [props.route.params.workspaceSignalId, archive, props.navigation]);

  const archiveDescription = useWorkspaceSignalArchiveDescription();

  return (
    <WorkspaceEditScheduledArchiveAt
      close={props.navigation.goBack}
      initialArchiveAt={data.workspace?.signal?.scheduledArchiveAt?.isoString}
      setOptions={props.navigation.setOptions}
      description={archiveDescription}
      onArchive={onArchive}
      archiveLoading={archiveLoading}
      onSave={onSave}
      saveLoading={saveLoading}
    />
  );
}

function WorkspaceSignalEditScheduledArchiveAtC(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalEditScheduledArchiveAt">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWorkspaceSignalEditScheduledArchiveAt
          fetchKey={fetchKey}
          {...props}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WorkspaceSignalEditScheduledArchiveAt = withWebModalStack(
  WorkspaceSignalEditScheduledArchiveAtC,
  true,
  {
    height: 350,
    width: 600,
  }
);
